import { Controller, useFormContext } from "react-hook-form";
import { Box, TextField } from "@mui/material";
import { createUseStyles } from "$utils/createUseStyles";
import { useLocation } from "react-router-dom";

import { useEffect } from "react";
import { theme } from "$utils/theme/bataillog";
import CField from "$forms/CField";
import useReferencing from "$hooks/useReferencing";
import { FieldValues } from "$hooks/useReferencingForm";
import { queryClient } from "$utils/queryClient";
import { ProductService } from "$services";
import { CacheInvalidationAction } from "$utils/cache";

type Props = {
  companyId: string;
  isReadOnly: boolean;
  nestIndex: number;
};

function OrderReferenceItem({ companyId, isReadOnly, nestIndex }: Props) {
  const classes = useStyles();
  const location = useLocation();
  const fieldPath = `orderReferences.${nestIndex}.receptionProduct` as const;
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext<FieldValues>();
  const { debouncedIsReferenceUnique } = useReferencing(companyId);

  // Check if we're in an EDB context
  const isInEdbContext = location.pathname.includes("/ebds");

  useEffect(() => {
    const reference = getValues(
      `orderReferences.${nestIndex}.productReference`
    );
    const currentData = getValues(`${fieldPath}`);

    if (reference && !currentData?.designation) {
      const fetchProduct = async () => {
        const filters = { reference, company: companyId };
        let product = await queryClient.getQueryData<any[]>([
          CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE,
          filters,
        ]);

        if (!product) {
          product = await queryClient.fetchQuery<any[]>({
            queryKey: [
              CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE,
              filters,
            ],
            queryFn: () => ProductService.getProductByReference(filters),
            staleTime: 1000 * 60,
          });
        }

        if (Array.isArray(product) && product.length > 0) {
          const productData = product[0];

          const categoryIds = Array.isArray(productData.categories)
            ? productData.categories.map((cat: any) =>
                typeof cat === "string" ? cat : cat.id || cat._id
              )
            : [];

          const pq = productData.quantity;
          const currentQty = currentData?.quantity;
          // Keep reception reference quantity when > 0; if 0, fall back to product quantity.
          const resolvedQuantity =
            typeof currentQty === "number" && currentQty > 0
              ? currentQty
              : pq !== undefined && pq !== null
                ? pq
                : typeof currentQty === "number"
                  ? currentQty
                  : (pq ?? 1);

          setValue(`${fieldPath}`, {
            reference,
            description: productData.description,
            designation: productData.designation,
            quantity: resolvedQuantity,
            unit: productData.unit,
            categories: categoryIds,
          });
        }
      };

      fetchProduct();
    }
  }, [nestIndex, companyId, fieldPath, getValues, setValue]);

  return (
    <Box className={classes.receptionReferenceForm}>
      <Box className={classes.formRow}>
        <Box flex={5}>
          <CField
            label="Référence"
            control={control}
            disabled={isReadOnly}
            name={`${fieldPath}.reference`}
            required
            error={
              errors.orderReferences &&
              errors.orderReferences[nestIndex]?.receptionProduct.reference
            }
            rules={{
              validate: {
                unique: async (value) =>
                  (await debouncedIsReferenceUnique(
                    getValues(`orderReferences.${nestIndex}.productReference`),
                    value
                  )) || "Un produit avec la même référence existe déjà.",
              },
            }}
          />
        </Box>
        <Box flex={5}>
          <CField
            label="Désignation"
            control={control}
            disabled={isReadOnly}
            name={`${fieldPath}.designation`}
            required
            error={
              errors.orderReferences &&
              errors.orderReferences[nestIndex]?.receptionProduct.designation
            }
          />
        </Box>
        <Box flex={3}>
          <CField
            control={control}
            error={
              errors.orderReferences &&
              errors.orderReferences[nestIndex]?.receptionProduct.unit
            }
            label="Unité"
            name={`${fieldPath}.unit`}
            required
            disabled
          />
        </Box>
        <Box flex={2}>
          <CField
            label="Quantité"
            control={control}
            type="number"
            disabled={isReadOnly || isInEdbContext}
            name={`${fieldPath}.quantity`}
            required
            error={
              errors.orderReferences &&
              errors.orderReferences[nestIndex]?.receptionProduct.quantity
            }
            rules={{
              valueAsNumber: true,
              min: { value: 1, message: "La quantité doit être au moins 1" },
            }}
          />
        </Box>
      </Box>

      <Box width="100%">
        <Controller
          control={control}
          name={`${fieldPath}.description`}
          render={({ field: { value, onChange } }) => (
            <TextField
              id="description"
              label="Description"
              variant="outlined"
              disabled={isReadOnly}
              multiline
              minRows={3}
              value={value}
              onChange={onChange}
              style={{ width: "100%" }}
              error={
                !!(
                  errors.orderReferences &&
                  errors.orderReferences[nestIndex]?.receptionProduct
                    .description
                )
              }
            />
          )}
        />
      </Box>
    </Box>
  );
}

const useStyles = createUseStyles(() => ({
  formRow: {
    display: "flex",
    gap: theme.spacing(2),
  },
  receptionReferenceForm: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));

export default OrderReferenceItem;
